import React, { PureComponent } from 'react'
import {
  node, string, bool, func, oneOfType, shape,
} from 'prop-types'
import humps from 'humps'
import { setupActiveWidget, globalEventBus, globalEvents } from '@active/widget'

import { withI18n } from '@active/react-utils/i18n'
import Button from '@active/react-ui/Button'

import ContentWithBanners from '../../../components/common/ContentWithBanners'
import { generateServices } from '../../../shared/service'
import { merge, omit, noop } from '../../../shared/utils/commonUtils'
import { commonServiceConfig, widgetTokenServiceConfig } from '../config'
import configureTrackBuilder from './configureTrackBuilder'
import facebookInit from './facebook'
import I18nMessage from '../../../components/common/I18nMessage'
import CommonError from '../../../components/common/CommonError'
import parseClientConfig from '../../../shared/utils/parseClientConfig'
import { addEvents, removeEvents } from './eventListeners'
import connectLocale from '../../../shared/utils/connectLocale'
import { show, hide } from '../../../shared/utils/spinnerUtils'

const DEFAULT_PROPS = {
  clientId: 'b579192a-617d-490d-94a6-6de26628c3ea',
  redirectUri: undefined,
  countryCodes: undefined,
  suppressActiveWaiver: undefined,
  useCustomizeWaiver: undefined,
  waiverAttachementObjectIds: undefined,
  fbAppId: undefined,
  fbVersion: undefined,
  recaptchaUrl: undefined,
  recaptchaSiteKey: undefined,
  realm: 'active',
  kcErrorMessage: undefined,
  kcMessageType: undefined,
  backLink: undefined,
  backLinkLabel: undefined,
  signinOption: undefined,

  widgetMountCompleteCallback: noop, // UT
}

const ACTIONS = [
  'signinSuccessCallback', 'signupSuccessCallback', 'newAccountCallback', 'existAccountCallback', 'logoutCallback',
  'forgotPwdSuccessCallback', 'resetPwdSuccessCallback', 'signupCountDownCallback',
  'waiverConsentsCallback', 'passwordConsentsCallback', 'countryConsentsCallback',
  'keycloakSigninCallback', 'confirmLinkCallback', 'idpProfileCallback', 'idpConfirmCallback', 'permissionConsentsCallback',
  'mfaCallback', 'phoneAuthenticationCallback', 'reviewProfileCallback', 'linkAccountCallback', 'linkAccountBackCallback',
  'keycloakSignupCallback', 'messageContentCallback',
]

function injectWidget({
  widget: WrappedComponent, serviceConfig = [], ftlMode = false, isCarryTokenOnHeader, enableActiveWidget, ignoreGetClientConfig = false,
}) {
  class WrapperComponent extends PureComponent {
    static propTypes = {
      locale: string.isRequired,
      clientId: string,
      redirectUri: string,
      countryCodes: string,
      suppressActiveWaiver: bool,
      useCustomizeWaiver: bool,
      waiverAttachementObjectIds: string,
      fbAppId: string,
      fbVersion: string,
      recaptchaUrl: string,
      recaptchaSiteKey: string,
      realm: string,
      kcErrorMessage: string,
      kcMessageType: string,
      backLink: string,
      backLinkLabel: string,
      signinOption: string,
      widgetMountCompleteCallback: func, // UT
      i18n: func.isRequired,
    }

    static defaultProps = DEFAULT_PROPS

    constructor(props) {
      super(props)
      const commonServices = generateServices(commonServiceConfig, {
        ...props,
        realm: props.realm || 'active',
      })
      const widgetServices = generateServices(widgetTokenServiceConfig, {
        ...props,
        realm: props.realm || 'active',
      })
      this.getWidgetToken = widgetServices.getWidgetToken
      this.getClientConfig = commonServices.getClientConfig
      this.configureTrack = configureTrackBuilder(props)
      this.state = {
        clientId: props.clientId,
        redirectUri: props.redirectUri,
        messageType: props.kcMessageType,
        messageText: props.kcErrorMessage,
        clientLoaded: false,
      }
    }

    componentDidMount() {
      if (enableActiveWidget) {
        setupActiveWidget()
        this.addActiveWidgetEvent()
      }
      const {
        locale, clientId,
        suppressActiveWaiver, useCustomizeWaiver, waiverAttachementObjectIds,
        fbAppId, fbVersion, recaptchaUrl, recaptchaSiteKey,
        signinOption,
        widgetMountCompleteCallback = noop, // UT
      } = this.props

      const { redirectUri } = this.state

      if (ignoreGetClientConfig) {
        this.setState({
          clientLoaded: true,
          clientConfig: {},
          redirectUri: redirectUri,
        }, () => widgetMountCompleteCallback({ hasError: false }))
      } else {
        this.getClientConfig({
          params: {
            client_id: clientId,
            locale,
            suppressActiveWaiver,
            useCustomizeWaiver,
            waiverAttachementObjectIds,
            fbAppId,
            fbVersion,
            recaptchaUrl,
            recaptchaSiteKey,
            signinOption,
          },
          resolve: response => {
            const { status, config: clientConfig } = parseClientConfig(response)
            if (status === 'SUCCESS') {
              const { url } = clientConfig
              this.setState({
                clientLoaded: true,
                clientConfig,
                redirectUri: redirectUri || url,
              }, () => widgetMountCompleteCallback({ hasError: false }))
            } else {
              this.setState({
                clientLoaded: true,
                messageText: 'passport.error.server.default',
              }, () => widgetMountCompleteCallback({ hasError: true }))
            }
          },
          reject: () => {
            this.setState({
              clientLoaded: true,
              messageText: 'passport.error.server.default',
            }, () => widgetMountCompleteCallback({ hasError: true }))
          },
        })
      }

      if (isCarryTokenOnHeader) {
        this.getWidgetToken({
          params: {},
          resolve: response => {
            this.setState({
              ...this.state,
              hasWidgetToken: true,
              epid: response.enterprisePersonId,
              widgetToken: response.widgetToken,
            })
          },
          reject: () => {
            this.setState({
              clientLoaded: true,
              hasWidgetToken: false,
              messageText: 'passport.error.server.default',
            }, () => widgetMountCompleteCallback({ hasError: true }))
          },
        })
      }

      addEvents()
    }

    componentWillUnmount() {
      enableActiveWidget && this.removeActiveWidgetEvent()
      removeEvents()
    }

    handleClick = () => {
      const { backLink } = this.props
      if (backLink) {
        window.location.href = backLink
      }
    }

    addActiveWidgetEvent = () => {
      globalEventBus.subscribe(globalEvents.LOAD_WIDGET_START, show)
      globalEventBus.subscribe(globalEvents.LOAD_WIDGET_SUCCESS, hide)
      globalEventBus.subscribe(globalEvents.LOAD_WIDGET_FAILED, hide)
    }

    removeActiveWidgetEvent = () => {
      globalEventBus.unsubscribe(globalEvents.LOAD_WIDGET_START, show)
      globalEventBus.unsubscribe(globalEvents.LOAD_WIDGET_SUCCESS, hide)
      globalEventBus.unsubscribe(globalEvents.LOAD_WIDGET_FAILED, hide)
    }

    render() {
      const {
        clientLoaded, clientConfig, clientId, redirectUri,
      } = this.state
      if (!clientLoaded) {
        return (<div />)
      }

      const {
        countryCodes, locale, widgetToken, ...rest
      } = this.props
      if (clientConfig) {
        const defaultParams = {
          clientId,
          redirectUri,
          locale,
          jsLocale: locale,
        }
        const actions = ACTIONS.reduce((acc, actionName) => Object.assign(acc, {
          [actionName]: rest[actionName],
        }), {})
        const services = generateServices(serviceConfig.map(config => merge(config, {
          params: merge(defaultParams, humps.decamelizeKeys(defaultParams)),
          headers: isCarryTokenOnHeader ? { widget_token: this.state.widgetToken } : {},
        })), this.props)
        const FacebookBtn = facebookInit(merge({
          countryCodes,
          clientConfig,
          actions,
          tracker: this.configureTrack,
        }, { clientId, redirectUri }, this.props))

        return (
          <WrappedComponent
            configureTrack={this.configureTrack}
            actions={actions}
            services={services}
            FacebookBtn={FacebookBtn}
            {...this.state}
            {...omit(this.props, ['actions'].concat(ACTIONS))}
          />
        )
      }
      // From ftl
      const { backLinkLabel, backLink } = this.props
      const { messageType, messageText } = this.state
      if (ftlMode) {
        return (
          <ContentWithBanners>
            <CommonError showError msgType={messageType} errMsg={messageText} />
            {
              backLink
              && (
                <Button onClick={this.handleClick}>
                  {backLinkLabel || <I18nMessage id="passport.common.btn.next" />}
                </Button>
              )
            }
          </ContentWithBanners>
        )
      }
      return (
        <CommonError showError msgType={messageType} errMsg={messageText} />
      )
    }
  }
  return withI18n(connectLocale(WrapperComponent))
}

injectWidget.propTypes = {
  widget: oneOfType([func, shape({}), node]).isRequired,
}

export default injectWidget
